import { Icons } from '@/components/icons';
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitiveRoot,
  AccordionTrigger,
} from '@/components/primitives/Accordion';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import AncillaryRow from '@/components/purchase/checkout/ancillaries/AncillaryRow';
import LegOriginDestination from '@/components/purchase/checkout/LegOriginDestination';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useAvailableAncillaries } from '@/hooks/useAvailableAncillaries';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { BookedAncillaryDTO, LegDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { type FC, useMemo } from 'react';

interface LegSectionProps {
  leg: LegDTO;
  classNames?: { accordionItem?: string };
  handleRemoveAncillary: (ancillary: BookedAncillaryDTO) => void;
  changeSelectedLeg: () => void;
}

const LegSection: FC<LegSectionProps> = ({
  leg,
  classNames,
  handleRemoveAncillary,
  changeSelectedLeg,
}) => {
  const booking = useSelector(purchaseFlowBookingSelector);

  const ancillariesByLegId = useMemo(() => {
    const ancillariesByLegId: Record<
      NonNullable<LegDTO['id']>,
      Array<BookedAncillaryDTO>
    > = {};

    booking?.bookedTrips?.forEach(({ bookedOffers }) =>
      bookedOffers?.forEach(({ admissions }) =>
        admissions?.forEach(({ coveredLegIds, ancillaries }) =>
          coveredLegIds?.forEach((id) => {
            ancillariesByLegId[id] = [
              ...(ancillariesByLegId[id] || []),
              ...(ancillaries || []),
            ];
          })
        )
      )
    );

    return ancillariesByLegId;
  }, [booking?.bookedTrips]);
  const availableAncillaries = useAvailableAncillaries(leg.id);
  const selectedAncillaries = leg.id ? ancillariesByLegId[leg.id] : [];

  return (
    <div>
      <LegOriginDestination
        origin={leg.originStop?.name ?? ''}
        destination={leg.destinationStop?.name ?? ''}
        ptMode={leg.ptMode}
      />
      <AccordionPrimitiveRoot type="single" defaultValue="open" collapsible>
        <AccordionItem
          value="open"
          className={cn('border-neutral-pale pb-4', classNames?.accordionItem)}
        >
          <AccordionTrigger
            className="my-4 flex items-center py-0 text-primary hover:no-underline"
            justifyBetween={false}
          >
            <Icons.addon className="h-5 w-5" />
            <Typography variant="body1-bold">
              <TransText i18nKey="addedExtras" />
            </Typography>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3 pl-7">
            {selectedAncillaries?.length ? (
              selectedAncillaries.map((ancillary, key) => (
                <AncillaryRow
                  ancillary={ancillary}
                  icon={
                    <Icons.addon
                      height={16}
                      width={16}
                      className="text-primary"
                    />
                  }
                  handleRemoveAncillary={handleRemoveAncillary}
                  key={key}
                />
              ))
            ) : (
              <Typography variant="body2" className="flex gap-2 text-neutral">
                <Icons.infoCircle width={16} height={16} />
                <TransText i18nKey="noExtrasAdded" />
              </Typography>
            )}

            {!!availableAncillaries.length && (
              <Button
                variant="secondary"
                data-testid="add-ancillary-button"
                className="mt-1"
                onClick={changeSelectedLeg}
              >
                <Icons.addon className="h-5 w-5" />
                <TransText i18nKey="addExtras" />
              </Button>
            )}
          </AccordionContent>
        </AccordionItem>
      </AccordionPrimitiveRoot>
    </div>
  );
};

export default LegSection;
